import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import useQuery from '_hooks/useQuery'
import orderApi from '_api/order'
import { updateDeliveryCombinedPayment } from '_redux/modules/delivery'

import {
  updateOrderCombinedPayment,
  updateOrderStatus,
  getPackingListEvents,
  removeIsPackingList,
  getListPackingList,
} from '_redux/modules/event'

import {
  handleDataOrder,
  handleDownloadPdf,
  handleDownloadExcel,
  handleDownloadExcelSummary,
  handleDataOrderByPercentage,
} from '_utils/function'
import {
  BUYER_PAID,
  BUYER_REFUNDED,
  BUYER_UNPAID,
  HOST_ORDER_CANCELED,
  HOST_ORDER_COLLECTED,
  HOST_ORDER_UNPAID,
  HOST_ORDER_VERIFIED,
  BUYER_ORDER_CANCELED,
} from '_utils/constant'

import { FiSearch } from 'react-icons/fi'

import HeaderPackingList from './components/HeaderPackingList'
import TablePackageList from './components/TablePackageList'
import './style.scss'

export default function PackingList() {
  const { addToast } = useToasts()
  const params = useQuery()
  const history = useHistory()
  const dispatch = useDispatch()
  const [packingList, setPackingList] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [downloadProgress, setDownloadProgress] = useState(null)
  const id = params.get('id') || null
  const query = useQuery()
  const isByAdmin = query.get('isByAdmin') === 'true'

  const { packListEvents, listPackingList, productList } = useSelector((state) => state.event)

  const [searchString, setSearchString] = useState('')
  const pack = listPackingList.find((e) => e.id === id)

  useEffect(() => {
    dispatch(getListPackingList)
  }, [dispatch])

  useEffect(() => {
    if (!listPackingList?.length) return
    setPackingList(pack)
    if (pack?.eventIds?.length) {
      dispatch(getPackingListEvents(pack.eventIds))
    }
    setIsLoading(false)
  }, [listPackingList])

  const handleSearchChange = (e) => {
    setSearchString(e.target.value)
  }

  const allOrders = useMemo(() => {
    const orderList = []
    packListEvents.forEach((event) => {
      const {
        orders,
        adminCost,
        discount,
        deliveryCost,
        productIdList,
        isPercentage = false,
        closingTime,
        title,
        pickupTime,
        groupName,
      } = event
      const { listOrder: otherListOrder } = isPercentage
        ? handleDataOrderByPercentage(orders, adminCost, discount, deliveryCost, productIdList)
        : handleDataOrder(orders, adminCost, discount, deliveryCost, productIdList)

      otherListOrder.forEach((order) => {
        if (order.status !== HOST_ORDER_CANCELED && order.buyerStatus !== BUYER_ORDER_CANCELED) {
          if (orderList.some((o) => o.uid === order.uid)) {
            const existingOrder = orderList.find((o) => o.uid === order.uid)
            existingOrder.refs.push(order.ref)
            existingOrder.events.push({
              title,
              groupName,
              pickupTime,
              closingTime,
            })
            existingOrder.statusPaid =
              order.status >= 2 ? existingOrder.statusPaid + 1 : existingOrder.statusPaid
            existingOrder.allProductPerOrder.push(...order.allProductPerOrder)
            existingOrder.totalFeeAllOrder += order.totalFee
            existingOrder.totalDeliveryPerOrder += order.deliveryPerOrder
            existingOrder.totalDiscountPerOrder += order.discountPerOrder
            existingOrder.totalPricePerOrder += order.totalPricePerOrder
            existingOrder.adminCostPerOrder += order.adminCostPerOrder
            existingOrder.orders.push({
              ref: order.ref,
              bookingAddress: order.bookingAddress,
              status: order.status,
              totalFee: order.totalFee,
              deliveryPerOrder: order.deliveryPerOrder,
              discountPerOrder: order.discountPerOrder,
              totalPricePerOrder: order.totalPricePerOrder,
              buyerStatus: order.buyerStatus,
              ePickupAddress: order.ePickupAddress,
              combinedPaymentRef: order.combinedPaymentRef,
              deliveryDate: order.deliveryDate,
              consignee: order.consignee,
              phoneConsignee: order.phoneConsignee,
            })
          } else {
            orderList.push({
              uid: order.uid,
              uName: order.uName,
              uEmail: order.uEmail,
              uPhone: order.uPhone,
              refs: [order.ref],
              events: [
                {
                  title,
                  groupName,
                  pickupTime,
                  closingTime,
                },
              ],
              statusPaid: order.status >= 2 ? 1 : 0,
              allProductPerOrder: order.allProductPerOrder,
              adminCostPerOrder: order.adminCostPerOrder,
              totalFeeAllOrder: order.totalFee,
              totalDeliveryPerOrder: order.deliveryPerOrder,
              totalDiscountPerOrder: order.discountPerOrder,
              totalPricePerOrder: order.totalPricePerOrder,
              orders: [
                {
                  ref: order.ref,
                  bookingAddress: order.bookingAddress,
                  status: order.status,
                  totalFee: order.totalFee,
                  deliveryPerOrder: order.deliveryPerOrder,
                  discountPerOrder: order.discountPerOrder,
                  totalPricePerOrder: order.totalPricePerOrder,
                  buyerStatus: order.buyerStatus,
                  ePickupAddress: order.ePickupAddress,
                  combinedPaymentRef: order.combinedPaymentRef,
                  deliveryDate: order.deliveryDate,
                  consignee: order.consignee,
                  phoneConsignee: order.phoneConsignee,
                },
              ],
            })
          }
        }
      })
    })
    return orderList
  }, [packListEvents])

  const listOrder = useMemo(() => {
    if (!searchString.trim()) return allOrders

    return allOrders.filter((order) => {
      const search = searchString.toLowerCase()
      return (
        order.uName?.toLowerCase().includes(search) ||
        order.uPhone?.toLowerCase().includes(search) ||
        order.ref?.toLowerCase().includes(search)
      )
    })
  }, [allOrders, searchString])

  const summary = useMemo(
    () => ({
      events: packListEvents.length,
      buyers: allOrders.length,
      items: allOrders.reduce(
        (acc, order) =>
          acc +
          (order.allProductPerOrder || []).reduce((sum, pro) => sum + (pro.pQuantity || 0), 0),
        0
      ),
    }),
    [packListEvents, allOrders]
  )

  const handleDelete = async () => {
    const listPackingListCount = listPackingList?.length
    const confirmed = window.confirm('Are you sure you want to delete this list ?')

    if (!confirmed) return

    await dispatch(removeIsPackingList(id))
    listPackingListCount > 1
      ? history.push('/event/list-packing-list')
      : history.push('/event/list-my-events')
  }

  const packToListPackingList = () => {
    history.push('/event/list-packing-list')
  }

  const updateOrderStatusAndBuyerStatusAPI = async (_ref, _data) => {
    dispatch(updateOrderStatus(id, _ref, _data.status, _data.buyerStatus))
    await orderApi.updateOrderStatusAndBuyerStatus(_ref, _data)
  }

  const updateOrderCombinedPaymentAPI = async (_data) => {
    dispatch(updateOrderCombinedPayment(_data.combinedPaymentRef, _data.status, _data.placerStatus))
    dispatch(
      updateDeliveryCombinedPayment(_data.combinedPaymentRef, _data.status, _data.placerStatus)
    )
    await orderApi.updateInvoicesStatus(_data)
  }

  const updateOrderStatusAPI = async (_ref, _data) => {
    dispatch(updateOrderStatus(id, _ref, _data.status, _data.buyerStatus))
    await orderApi.updateOrderStatusAndBuyerStatus(_ref, { status: _data.status })
  }

  const onChangeSelect = async (e, ref, _status, _buyerStatus, _combinedPaymentRef) => {
    try {
      if (
        _status === HOST_ORDER_UNPAID &&
        (Number(e.target.value) === HOST_ORDER_VERIFIED ||
          Number(e.target.value) === HOST_ORDER_COLLECTED)
      ) {
        const _data = { status: Number(e.target.value), buyerStatus: BUYER_PAID }
        await updateOrderStatusAndBuyerStatusAPI(ref, _data)
        dispatch(getPackingListEvents(pack.eventIds))
      } else if (
        _status !== HOST_ORDER_CANCELED &&
        Number(e.target.value) === HOST_ORDER_CANCELED &&
        _buyerStatus > BUYER_UNPAID
      ) {
        if (_combinedPaymentRef) {
          const _data = {
            combinedPaymentRef: _combinedPaymentRef,
            status: HOST_ORDER_CANCELED,
            placerStatus: BUYER_REFUNDED,
          }
          await updateOrderCombinedPaymentAPI(_data)
          dispatch(getPackingListEvents(pack.eventIds))
        } else {
          const _data = { status: Number(e.target.value), buyerStatus: BUYER_REFUNDED }
          await updateOrderStatusAndBuyerStatusAPI(ref, _data)
          dispatch(getPackingListEvents(pack.eventIds))
        }
      } else if (
        _status === HOST_ORDER_CANCELED &&
        Number(e.target.value) !== HOST_ORDER_CANCELED &&
        _buyerStatus === BUYER_REFUNDED
      ) {
        const _data = { status: Number(e.target.value), buyerStatus: BUYER_PAID }
        await updateOrderStatusAndBuyerStatusAPI(ref, _data)
        dispatch(getPackingListEvents(pack.eventIds))
      } else {
        const _data = { status: Number(e.target.value), buyerStatus: _buyerStatus }
        await updateOrderStatusAPI(ref, _data)
        dispatch(getPackingListEvents(pack.eventIds))
      }
    } catch (error) {
      const { msgResp } = error
      addToast(msgResp, { appearance: 'error', autoDismiss: true })
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <div className='packing-list'>
      <HeaderPackingList
        packingList={packingList}
        setPackingList={setPackingList}
        summary={summary}
        onBack={packToListPackingList}
        onExport={() =>
          handleDownloadPdf(listOrder, packingList.name, productList, (progress) => {
            if (progress === 100 || progress === -1) {
              setDownloadProgress(null)
            } else {
              setDownloadProgress(Math.round(progress))
            }
          })
        }
        onExportExcel={() => handleDownloadExcel(listOrder, packingList.name)}
        onExportExcelSummary={() => handleDownloadExcelSummary(listOrder, packingList.name)}
        onDelete={handleDelete}
        loadingExport={downloadProgress}
      />
      <div className='packing-list-search'>
        <FiSearch size={16} className='packing-list-search-icon' />
        <input
          type='text'
          className='packing-list-search-input'
          placeholder='Search name, mobile or order ref'
          value={searchString}
          onChange={handleSearchChange}
        />
      </div>
      <TablePackageList
        listOrder={listOrder}
        isByAdmin={isByAdmin}
        onChangeSelect={onChangeSelect}
      />
    </div>
  )
}
